/**
 * Realtime Service
 * Manages Supabase realtime subscriptions and smart data refresh strategies
 * across the entire application
 */

import { supabase } from '../utils/supabase/client';
import { DataService } from './DataService';

// Development mode - set to true to see verbose logs
const DEV_MODE = false;

type SubscriptionCallback = (payload: any) => void;
type TableName = 
  | 'projects' 
  | 'companies' 
  | 'users' 
  | 'contract_items' 
  | 'interim_applications'
  | 'retention_ledgers'
  | 'tasks'
  | 'notifications'
  | 'share_tokens'
  | 'rate_libraries'
  | 'main_contractors'
  | 'contacts'
  | 'activities'
  | 'documents'
  | 'claims'
  | 'boq_documents'
  | 'kv_store_a476273b';

interface SubscriptionOptions {
  table: TableName;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  onInsert?: SubscriptionCallback;
  onUpdate?: SubscriptionCallback;
  onDelete?: SubscriptionCallback;
  onChange?: SubscriptionCallback;
}

interface RealtimeConfig {
  enabled: boolean;
  pollInterval?: number;
  pauseWhenEditing?: boolean;
  pauseWhenFormOpen?: boolean;
  showUpdateNotifications?: boolean;
}

class RealtimeServiceClass {
  private channels: Map<string, any> = new Map();
  private config: RealtimeConfig = {
    enabled: true,
    pollInterval: 30000, // 30 seconds fallback polling
    pauseWhenEditing: true,
    pauseWhenFormOpen: true,
    showUpdateNotifications: false,
  };
  
  // Track user activity state
  private isEditing = false;
  private isFormOpen = false;
  private lastActivity = Date.now();
  private activityTimeout: NodeJS.Timeout | null = null;
  
  // Track pending updates
  private pendingUpdates: Map<string, any[]> = new Map();
  
  // Global listeners for updates
  private globalListeners: Set<(updates: any) => void> = new Set();

  constructor() {
    this.setupActivityTracking();
    this.setupVisibilityChange();
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  /**
   * Update realtime configuration
   */
  public configure(options: Partial<RealtimeConfig>) {
    this.config = { ...this.config, ...options };
  }

  /**
   * Get current configuration
   */
  public getConfig(): RealtimeConfig {
    return { ...this.config };
  }

  // ============================================================================
  // ACTIVITY TRACKING
  // ============================================================================

  /**
   * Set editing state (pauses updates if configured)
   */
  public setEditing(editing: boolean) {
    this.isEditing = editing;
    if (DEV_MODE) console.log(`🔄 Realtime: Editing state = ${editing}`);
  }

  /**
   * Set form open state (pauses updates if configured)
   */
  public setFormOpen(open: boolean) {
    this.isFormOpen = open;
    if (DEV_MODE) console.log(`🔄 Realtime: Form open state = ${open}`);
  }

  /**
   * Check if updates should be paused
   */
  private shouldPauseUpdates(): boolean {
    if (!this.config.enabled) return true;
    if (this.config.pauseWhenEditing && this.isEditing) return true;
    if (this.config.pauseWhenFormOpen && this.isFormOpen) return true;
    return false;
  }

  /**
   * Setup activity tracking (mouse, keyboard, touch)
   */
  private setupActivityTracking() {
    if (typeof window === 'undefined') return;

    const scheduleInactivityCheck = () => {
      if (this.activityTimeout) return;
      this.activityTimeout = setTimeout(() => {
        this.activityTimeout = null;
        const remaining = 30000 - (Date.now() - this.lastActivity);
        if (remaining > 0) {
          this.activityTimeout = setTimeout(() => {
            this.activityTimeout = null;
            if (Date.now() - this.lastActivity >= 30000) {
              this.setEditing(false);
            } else {
              scheduleInactivityCheck();
            }
          }, remaining);
          return;
        }
        this.setEditing(false);
      }, 30000);
    };

    const trackActivity = () => {
      this.lastActivity = Date.now();
      scheduleInactivityCheck();
    };

    // Track various user interactions
    window.addEventListener('mousemove', trackActivity, { passive: true });
    window.addEventListener('keydown', trackActivity, { passive: true });
    window.addEventListener('click', trackActivity, { passive: true });
    window.addEventListener('touchstart', trackActivity, { passive: true });
  }

  /**
   * Setup visibility change detection (tab switching)
   */
  private setupVisibilityChange() {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        if (DEV_MODE) console.log('🔄 Realtime: Tab became visible, refreshing data...');
        this.notifyGlobalListeners({ type: 'visibility_refresh' });
      }
    });
  }

  // ============================================================================
  // SUBSCRIPTION MANAGEMENT
  // ============================================================================

  /**
   * Subscribe to realtime changes for a specific table
   */
  public subscribe(options: SubscriptionOptions): () => void {
    // Demo session: never open postgres_changes channels — these stream
    // every other tenant's row mutations into the demo client. The
    // unsubscribe function still needs to be valid, hence the noop.
    if (DataService.isDemoMode()) {
      if (DEV_MODE) console.log(`🚫 Realtime: skipping ${options.table} (demo mode)`);
      return () => {};
    }

    const channelName = `${options.table}-${Date.now()}-${Math.random()}`;

    if (DEV_MODE) console.log(`📡 Realtime: Subscribing to ${options.table} changes...`);

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: options.event || '*',
          schema: 'public',
          table: options.table,
          filter: options.filter,
        },
        (payload) => {
          if (DEV_MODE) console.log(`📬 Realtime: Received ${payload.eventType} on ${options.table}`, payload);
          
          // Store update if paused
          if (this.shouldPauseUpdates()) {
            if (!this.pendingUpdates.has(options.table)) {
              this.pendingUpdates.set(options.table, []);
            }
            const updates = this.pendingUpdates.get(options.table)!;
            updates.push(payload);
            
            // Limit pending updates to prevent memory leaks if user is idle/form open for days
            if (updates.length > 100) {
              this.pendingUpdates.set(options.table, updates.slice(-100));
            }
            
            if (DEV_MODE) console.log(`⏸️  Realtime: Update paused for ${options.table}`);
            return;
          }

          // Process update immediately
          this.processUpdate(payload, options);
        }
      )
      .subscribe((status) => {
        if (DEV_MODE) console.log(`📡 Realtime: Subscription status for ${options.table}:`, status);
      });

    this.channels.set(channelName, channel);

    // Return unsubscribe function
    return () => {
      if (DEV_MODE) console.log(`📡 Realtime: Unsubscribing from ${options.table}`);
      supabase.removeChannel(channel);
      this.channels.delete(channelName);
    };
  }

  /**
   * Subscribe to multiple tables at once
   */
  public subscribeMultiple(subscriptions: SubscriptionOptions[]): () => void {
    const unsubscribeFns = subscriptions.map(sub => this.subscribe(sub));
    
    return () => {
      unsubscribeFns.forEach(fn => fn());
    };
  }

  /**
   * Process an update and call appropriate callbacks
   */
  private processUpdate(payload: any, options: SubscriptionOptions) {
    const { eventType } = payload;

    // Call specific event handlers
    if (eventType === 'INSERT' && options.onInsert) {
      options.onInsert(payload);
    } else if (eventType === 'UPDATE' && options.onUpdate) {
      options.onUpdate(payload);
    } else if (eventType === 'DELETE' && options.onDelete) {
      options.onDelete(payload);
    }

    // Call general change handler
    if (options.onChange) {
      options.onChange(payload);
    }

    // Notify global listeners
    this.notifyGlobalListeners({
      table: options.table,
      eventType,
      payload,
    });
  }

  /**
   * Process all pending updates
   */
  public processPendingUpdates() {
    if (this.pendingUpdates.size === 0) return;

    if (DEV_MODE) {
      console.log(`🔄 Realtime: Processing ${this.pendingUpdates.size} pending updates...`);
      
      this.pendingUpdates.forEach((updates, table) => {
        console.log(`  - ${table}: ${updates.length} updates`);
      });
    }

    this.notifyGlobalListeners({ type: 'pending_updates', updates: this.pendingUpdates });
    this.pendingUpdates.clear();
  }

  /**
   * Get count of pending updates
   */
  public getPendingUpdateCount(): number {
    let count = 0;
    this.pendingUpdates.forEach(updates => {
      count += updates.length;
    });
    return count;
  }

  /**
   * Check if there are pending updates for a specific table
   */
  public hasPendingUpdates(table: TableName): boolean {
    return this.pendingUpdates.has(table) && this.pendingUpdates.get(table)!.length > 0;
  }

  // ============================================================================
  // GLOBAL LISTENERS
  // ============================================================================

  /**
   * Add a global listener for all realtime updates
   */
  public addGlobalListener(listener: (update: any) => void): () => void {
    this.globalListeners.add(listener);
    
    return () => {
      this.globalListeners.delete(listener);
    };
  }

  /**
   * Notify all global listeners
   */
  private notifyGlobalListeners(update: any) {
    this.globalListeners.forEach(listener => {
      try {
        listener(update);
      } catch (error) {
        console.error('Error in global realtime listener:', error);
      }
    });
  }

  // ============================================================================
  // OPTIMISTIC UPDATES
  // ============================================================================

  /**
   * Helper for optimistic UI updates
   */
  public async optimisticUpdate<T>(
    localUpdate: () => void,
    serverUpdate: () => Promise<T>,
    rollback: () => void
  ): Promise<T | null> {
    try {
      // Apply local update immediately
      localUpdate();
      
      // Try server update
      const result = await serverUpdate();
      
      return result;
    } catch (error) {
      console.error('Optimistic update failed, rolling back:', error);
      rollback();
      return null;
    }
  }

  // ============================================================================
  // POLLING FALLBACK
  // ============================================================================

  /**
   * Setup polling as a fallback (useful if realtime fails)
   */
  public setupPolling(callback: () => void, interval?: number): () => void {
    const pollInterval = interval || this.config.pollInterval || 30000;
    
    if (DEV_MODE) console.log(`⏱️  Realtime: Setting up polling fallback (${pollInterval}ms)`);
    
    const intervalId = setInterval(() => {
      if (!this.shouldPauseUpdates()) {
        if (DEV_MODE) console.log('⏱️  Realtime: Polling for updates...');
        callback();
      }
    }, pollInterval);

    return () => {
      clearInterval(intervalId);
    };
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Unsubscribe from all channels
   */
  public unsubscribeAll() {
    if (DEV_MODE) console.log(`📡 Realtime: Unsubscribing from all channels (${this.channels.size})`);
    
    this.channels.forEach((channel) => {
      supabase.removeChannel(channel);
    });
    
    this.channels.clear();
    this.pendingUpdates.clear();
    this.globalListeners.clear();
  }

  /**
   * Cleanup activity tracking
   */
  public cleanup() {
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
    this.unsubscribeAll();
  }
}

// Export singleton instance
export const RealtimeService = new RealtimeServiceClass();
